<template>
    <Primitive
        :as="as"
        :class="classes"
    >
        <div :class="element('inner')">
            <div
                v-if="$slots.logo"
                :class="element('brand')"
            >
                <slot name="logo" />
            </div>

            <NavigationMenuRoot
                v-if="items.length"
                v-model="openValue"
                :class="element('menu')"
                :delay-duration="120"
            >
                <NavigationMenuList :class="element('list')">
                    <NavigationMenuItem
                        v-for="(item, index) in items"
                        :key="item.label"
                        :ref="itemRef(index)"
                        :value="String(index)"
                        :class="element('item')"
                    >
                        <template v-if="item.items?.length">
                            <!--
                                A linked parent splits into a real link and an
                                icon-only toggle: an anchor cannot nest inside the
                                trigger's button, and a navigating trigger would
                                strand the panel on touch. Only the toggle opens
                                the panel.
                            -->
                            <div
                                v-if="item.href"
                                :class="element('split', modifier('open', openValue === String(index)))"
                            >
                                <NavigationMenuLink
                                    :as="linkAs"
                                    :active="item.current"
                                    :class="element('link', modifier('current', isBranchCurrent(item)))"
                                    v-bind="linkAttrs(item)"
                                >
                                    {{ item.label }}
                                </NavigationMenuLink>

                                <NavigationMenuTrigger
                                    :class="element('link', modifier('trigger'), modifier('icon-only'))"
                                    :aria-label="submenuLabel(item.label)"
                                >
                                    <span
                                        :class="element('chevron')"
                                        aria-hidden="true"
                                    >
                                        <slot name="chevron">
                                            <svg
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                stroke-width="2"
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                            >
                                                <path d="m6 9 6 6 6-6" />
                                            </svg>
                                        </slot>
                                    </span>
                                </NavigationMenuTrigger>
                            </div>

                            <NavigationMenuTrigger
                                v-else
                                :class="triggerClasses(item)"
                            >
                                {{ item.label }}

                                <span
                                    :class="element('chevron')"
                                    aria-hidden="true"
                                >
                                    <slot name="chevron">
                                        <svg
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            stroke-width="2"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                        >
                                            <path d="m6 9 6 6 6-6" />
                                        </svg>
                                    </slot>
                                </span>
                            </NavigationMenuTrigger>

                            <NavigationMenuContent
                                :class="panelClasses(index)"
                                :style="panelStyle(index)"
                            >
                                <ul :class="element('panel-list')">
                                    <li
                                        v-for="child in item.items"
                                        :key="child.label"
                                    >
                                        <NavigationMenuLink
                                            :as="linkAs"
                                            :active="child.current"
                                            :class="element('panel-link')"
                                            v-bind="linkAttrs(child)"
                                        >
                                            <span :class="element('panel-label')">{{ child.label }}</span>

                                            <span
                                                v-if="child.description"
                                                :class="element('panel-description')"
                                            >{{ child.description }}</span>
                                        </NavigationMenuLink>
                                    </li>
                                </ul>
                            </NavigationMenuContent>
                        </template>

                        <NavigationMenuLink
                            v-else
                            :as="linkAs"
                            :active="item.current"
                            :class="element('link')"
                            v-bind="linkAttrs(item)"
                        >
                            {{ item.label }}
                        </NavigationMenuLink>
                    </NavigationMenuItem>

                    <div
                        :class="element('indicator')"
                        :style="indicatorStyle"
                        :data-state="indicatorVisible ? 'visible' : 'hidden'"
                        aria-hidden="true"
                    >
                        <div :class="element('indicator-bar')" />
                    </div>
                </NavigationMenuList>
            </NavigationMenuRoot>

            <div
                v-if="$slots.search"
                :class="element('search')"
            >
                <slot name="search" />
            </div>

            <div
                v-if="$slots.actions"
                :class="element('actions')"
            >
                <slot name="actions" />
            </div>

            <DialogRoot v-model:open="drawerOpen">
                <DialogTrigger
                    :class="element('toggle')"
                    :aria-label="menuLabel"
                >
                    <span aria-hidden="true">
                        <slot
                            v-if="drawerOpen"
                            name="close-icon"
                        >
                            <svg
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            >
                                <path d="M18 6 6 18M6 6l12 12" />
                            </svg>
                        </slot>

                        <slot
                            v-else
                            name="menu-icon"
                        >
                            <svg
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            >
                                <path d="M4 6h16M4 12h16M4 18h16" />
                            </svg>
                        </slot>
                    </span>
                </DialogTrigger>

                <DialogPortal>
                    <DialogOverlay :class="element('scrim')" />

                    <DialogContent :class="element('drawer')">
                        <VisuallyHidden as-child>
                            <DialogTitle>{{ drawerLabel }}</DialogTitle>
                        </VisuallyHidden>

                        <VisuallyHidden as-child>
                            <DialogDescription>{{ drawerDescription }}</DialogDescription>
                        </VisuallyHidden>

                        <div :class="element('drawer-head')">
                            <DialogClose
                                :class="element('drawer-close')"
                                :aria-label="closeLabel"
                            >
                                <span aria-hidden="true">
                                    <slot name="close-icon">
                                        <svg
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            stroke-width="2"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                        >
                                            <path d="M18 6 6 18M6 6l12 12" />
                                        </svg>
                                    </slot>
                                </span>
                            </DialogClose>
                        </div>

                        <div
                            v-if="$slots.search"
                            :class="element('drawer-search')"
                        >
                            <slot name="search" />
                        </div>

                        <nav :class="element('drawer-nav')">
                            <ul :class="element('drawer-list')">
                                <li
                                    v-for="item in items"
                                    :key="item.label"
                                >
                                    <CollapsibleRoot
                                        v-if="item.items?.length"
                                        :class="element('drawer-group')"
                                    >
                                        <!--
                                            Same split as in the bar: a linked
                                            parent stays directly clickable and
                                            the chevron alone toggles the group.
                                        -->
                                        <div
                                            v-if="item.href"
                                            :class="element('drawer-split')"
                                        >
                                            <component
                                                :is="linkAs"
                                                :class="element('drawer-link')"
                                                :aria-current="item.current ? 'page' : undefined"
                                                v-bind="linkAttrs(item)"
                                                @click="drawerOpen = false"
                                            >
                                                {{ item.label }}
                                            </component>

                                            <CollapsibleTrigger
                                                :class="element('drawer-trigger', modifier('icon-only'))"
                                                :aria-label="submenuLabel(item.label)"
                                            >
                                                <span
                                                    :class="element('chevron')"
                                                    aria-hidden="true"
                                                >
                                                    <slot name="chevron">
                                                        <svg
                                                            viewBox="0 0 24 24"
                                                            fill="none"
                                                            stroke="currentColor"
                                                            stroke-width="2"
                                                            stroke-linecap="round"
                                                            stroke-linejoin="round"
                                                        >
                                                            <path d="m6 9 6 6 6-6" />
                                                        </svg>
                                                    </slot>
                                                </span>
                                            </CollapsibleTrigger>
                                        </div>

                                        <CollapsibleTrigger
                                            v-else
                                            :class="element('drawer-trigger')"
                                        >
                                            {{ item.label }}

                                            <span
                                                :class="element('chevron')"
                                                aria-hidden="true"
                                            >
                                                <slot name="chevron">
                                                    <svg
                                                        viewBox="0 0 24 24"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        stroke-width="2"
                                                        stroke-linecap="round"
                                                        stroke-linejoin="round"
                                                    >
                                                        <path d="m6 9 6 6 6-6" />
                                                    </svg>
                                                </slot>
                                            </span>
                                        </CollapsibleTrigger>

                                        <CollapsibleContent :class="element('drawer-panel')">
                                            <ul :class="element('drawer-sublist')">
                                                <li
                                                    v-for="child in item.items"
                                                    :key="child.label"
                                                >
                                                    <component
                                                        :is="linkAs"
                                                        :class="element('drawer-sublink')"
                                                        :aria-current="child.current ? 'page' : undefined"
                                                        v-bind="linkAttrs(child)"
                                                        @click="drawerOpen = false"
                                                    >
                                                        {{ child.label }}
                                                    </component>
                                                </li>
                                            </ul>
                                        </CollapsibleContent>
                                    </CollapsibleRoot>

                                    <component
                                        :is="linkAs"
                                        v-else
                                        :class="element('drawer-link')"
                                        :aria-current="item.current ? 'page' : undefined"
                                        v-bind="linkAttrs(item)"
                                        @click="drawerOpen = false"
                                    >
                                        {{ item.label }}
                                    </component>
                                </li>
                            </ul>
                        </nav>

                        <div
                            v-if="$slots['drawer-footer']"
                            :class="element('drawer-footer')"
                        >
                            <slot name="drawer-footer" />
                        </div>
                    </DialogContent>
                </DialogPortal>
            </DialogRoot>
        </div>
    </Primitive>
</template>

<script setup lang="ts">
import type { KNavigationItem, KNavigationProps } from './types'
import useBem from '@yeeroen/use-bem'
import {
    CollapsibleContent,
    CollapsibleRoot,
    CollapsibleTrigger,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogOverlay,
    DialogPortal,
    DialogRoot,
    DialogTitle,
    DialogTrigger,
    NavigationMenuContent,
    NavigationMenuItem,
    NavigationMenuLink,
    NavigationMenuList,
    NavigationMenuRoot,
    NavigationMenuTrigger,
    Primitive,
    VisuallyHidden,
} from 'reka-ui'

import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'

defineOptions({ name: 'KNavigation' })

const props = withDefaults(defineProps<KNavigationProps>(), {
    as: 'header',
    items: () => [],
    linkAs: 'a',
    sticky: false,
    menuLabel: 'Open menu',
    drawerLabel: 'Menu',
    drawerDescription: 'Site navigation',
    closeLabel: 'Close menu',
    submenuLabel: (label: string) => `Open ${label} submenu`,
})

defineSlots<{
    /** Brand mark. Owns its own link, image and alt text. */
    'logo'?: () => unknown
    /**
     * Search form. Rendered twice — once in the bar and once in the mobile
     * drawer — but never active twice: the bar copy is `display: none` below
     * the tablet breakpoint, and the drawer copy only mounts while open.
     */
    'search'?: () => unknown
    /** Trailing controls, beside the search. */
    'actions'?: () => unknown
    /** Extra content at the bottom of the mobile drawer. */
    'drawer-footer'?: () => unknown
    'menu-icon'?: () => unknown
    'close-icon'?: () => unknown
    'chevron'?: () => unknown
}>()

const drawerOpen = ref(false)

const { block, element, modifier } = useBem('k-navigation')

const classes = computed(() => block(
    modifier('sticky', props.sticky),
    modifier('drawer-open', drawerOpen.value),
))

function triggerClasses(item: KNavigationItem) {
    return element('link', modifier('trigger'), modifier('current', isBranchCurrent(item)))
}

// Mirrors `min-width` on the panel list, which sets the narrowest a panel gets.
const PANEL_MIN_WIDTH = 224

/**
 * Panels and the sliding indicator are positioned against the list rather than
 * their own item, because a positioned item would become its own offsetParent
 * and zero out the offsetLeft read here. So each item's inline offset and
 * width are measured and handed to CSS.
 */
const itemElements = new Map<number, HTMLElement>()
const itemRects = ref<Record<number, { offset: number, width: number }>>({})
const listWidth = ref(0)

let observer: ResizeObserver | undefined
let unmounting = false

// Stable ref callback per index: an inline `:ref="el => …"` creates a fresh
// function each render, so Vue re-fires the stale one against a half-unmounted
// instance during teardown (HMR / route transitions) and crashes.
const itemRefs = new Map<number, (element: unknown) => void>()

function itemRef(index: number) {
    let register = itemRefs.get(index)

    if (!register) {
        register = (element: unknown) => registerItem(index, element)
        itemRefs.set(index, register)
    }

    return register
}

function registerItem(index: number, element: unknown) {
    if (unmounting)
        return

    const node = (element as { $el?: unknown } | null)?.$el ?? element

    if (node instanceof HTMLElement) {
        itemElements.set(index, node)
    } else {
        itemElements.delete(index)
    }
}

function measure() {
    const rects: Record<number, { offset: number, width: number }> = {}

    for (const [index, node] of itemElements) {
        rects[index] = { offset: node.offsetLeft, width: node.offsetWidth }
    }

    itemRects.value = rects
    listWidth.value = [...itemElements.values()][0]?.parentElement?.offsetWidth ?? 0
}

function panelStyle(index: number) {
    const offset = itemRects.value[index]?.offset

    return offset === undefined ? undefined : { '--navigation-panel-offset': `${offset}px` }
}

/**
 * A panel with less room beside its trigger than its own minimum width is
 * flipped to the trailing edge — the collision handling Reka's shared viewport
 * would otherwise have done.
 */
function panelClasses(index: number) {
    const offset = itemRects.value[index]?.offset ?? 0
    const flip = listWidth.value > 0 && listWidth.value - offset < PANEL_MIN_WIDTH

    return element('panel', modifier('align-end', flip))
}

/**
 * Reka's own indicator measures the active trigger, which would underline only
 * the chevron of a split item. This one reads the item rectangles that panel
 * positioning already measures, so the underline always spans the whole item.
 * The v-model is passive: Reka keeps driving every open/close interaction.
 */
const openValue = ref('')
const activeIndicatorIndex = ref<number>()

watch(openValue, (value) => {
    if (value === '')
        return

    const index = Number(value)

    if (Number.isInteger(index) && itemElements.has(index)) {
        activeIndicatorIndex.value = index
    }
})

const indicatorVisible = computed(() => openValue.value !== '')

const indicatorStyle = computed(() => {
    const rect = activeIndicatorIndex.value === undefined
        ? undefined
        : itemRects.value[activeIndicatorIndex.value]

    return rect && {
        '--navigation-indicator-position': `${rect.offset}px`,
        '--navigation-indicator-size': `${rect.width}px`,
    }
})

onMounted(() => {
    measure()

    // The bar reflows with the viewport and with webfont swaps, so a single
    // measurement on mount would drift out of date.
    if (typeof ResizeObserver !== 'undefined') {
        observer = new ResizeObserver(measure)

        for (const node of itemElements.values()) {
            observer.observe(node)
        }
    }
})

onBeforeUnmount(() => {
    unmounting = true
    observer?.disconnect()
})

watch(() => props.items, () => nextTick(measure), { deep: true })

/**
 * A parent is current when it, or any of its children, is. Without this a
 * dropdown loses every trace of the active page once collapsed.
 */
function isBranchCurrent(item: KNavigationItem) {
    return Boolean(item.current) || Boolean(item.items?.some(child => child.current))
}

function linkAttrs(item: KNavigationItem) {
    return {
        href: item.href,
        target: item.external ? '_blank' : undefined,
        rel: item.external ? 'noopener noreferrer' : undefined,
    }
}
</script>

<style lang="scss">
@use './k-navigation.scss';
</style>
